import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Landmark,
  Copy,
  Check,
  Download,
  Mail,
  AlertCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ordersAPI, paymentsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';
import { money } from '@/lib/format';

/**
 * EFT instructions page — shown to a customer who chose "EFT" at checkout.
 * Pulls banking details + amount + reference from the backend (admin-configured).
 *
 * Route: /eft/:orderId
 */
export default function EftInstructionsPage() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [eft, setEft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedField, setCopiedField] = useState('');
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    paymentsAPI
      .getEftInstructions(orderId)
      .then((r) => setEft(r.data))
      .catch((e) =>
        setError(e?.response?.data?.detail || 'Could not load EFT instructions')
      )
      .finally(() => setLoading(false));
  }, [orderId, user, navigate]);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      toast.success(`${label} copied`);
      setTimeout(() => setCopiedField(''), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const res = await ordersAPI.downloadInvoice(orderId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Invoice_${orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download invoice');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 text-teal animate-spin" />
      </div>
    );
  }

  if (error || !eft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div
          data-testid="eft-error"
          className="max-w-md w-full p-8 rounded-2xl bg-card border border-border text-center"
        >
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="font-heading text-xl text-foreground mb-2">Could not load EFT details</h2>
          <p className="font-body text-sm text-foreground/60 mb-6">{error || 'Unknown error'}</p>
          <Link to="/profile">
            <Button variant="ctaSecondary" className="rounded-full font-body text-sm">
              Back to my orders
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const alreadyPaid = eft.payment_status === 'paid';

  return (
    <div data-testid="eft-page" className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <Link
          to="/profile"
          className="inline-flex items-center gap-1.5 font-body text-xs text-foreground/60 hover:text-foreground mb-6"
          data-testid="eft-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to my account
        </Link>

        <div className="mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="h-[2px] w-8 bg-gold rounded-full" />
            <p className="font-body text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gold font-bold">
              EFT payment
            </p>
          </div>
          <h1 className="font-heading text-2xl sm:text-4xl font-light text-foreground tracking-tight">
            Complete your bank transfer
          </h1>
          <p className="font-body text-sm text-foreground/60 mt-2">
            Use the reference below so we can match your payment to your order. We'll send a confirmation
            email once the funds reflect and an admin marks it received.
          </p>
        </div>

        {alreadyPaid && (
          <div
            data-testid="eft-paid-banner"
            className="mb-6 p-4 rounded-xl bg-teal/15 border border-teal/40 flex items-start gap-3"
          >
            <Check className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-body text-sm text-foreground font-semibold">
                This order is already marked as paid.
              </p>
              <p className="font-body text-xs text-foreground/60 mt-0.5">
                No further action needed.
              </p>
            </div>
          </div>
        )}

        {/* Reference + Amount */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6"
          data-testid="eft-summary"
        >
          <div className="p-5 rounded-2xl bg-gold/10 border border-gold/30">
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-1">
              Reference (use this!)
            </p>
            <div className="flex items-center justify-between gap-2">
              <p
                data-testid="eft-reference"
                className="font-heading text-2xl sm:text-3xl text-foreground tracking-wide"
              >
                {eft.reference}
              </p>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => copy(eft.reference, 'Reference')}
                data-testid="copy-eft-reference"
                className="h-9 w-9 rounded-full hover:bg-gold/20"
              >
                {copiedField === 'Reference' ? (
                  <Check className="h-4 w-4 text-teal" />
                ) : (
                  <Copy className="h-4 w-4 text-gold" />
                )}
              </Button>
            </div>
          </div>
          <div className="p-5 rounded-2xl bg-teal/10 border border-teal/30">
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-teal font-bold mb-1">
              Amount due
            </p>
            <div className="flex items-center justify-between gap-2">
              <p
                data-testid="eft-amount"
                className="font-heading text-2xl sm:text-3xl text-foreground"
              >
                R{money(Number(eft.amount))}
              </p>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => copy(money(Number(eft.amount)), 'Amount')}
                data-testid="copy-eft-amount"
                className="h-9 w-9 rounded-full hover:bg-teal/20"
              >
                {copiedField === 'Amount' ? (
                  <Check className="h-4 w-4 text-teal" />
                ) : (
                  <Copy className="h-4 w-4 text-teal" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Bank details */}
        <div className="p-5 sm:p-6 rounded-2xl bg-card border border-border mb-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-teal" />
              <h2 className="font-heading text-lg text-foreground">Banking details</h2>
            </div>
            <Button
              variant="ctaSecondary"
              size="sm"
              onClick={() => copy(eft.bank_details, 'Banking details')}
              disabled={!eft.bank_details}
              data-testid="copy-bank-details"
              className="rounded-full font-body text-xs gap-1.5"
            >
              {copiedField === 'Banking details' ? (
                <><Check className="h-3.5 w-3.5 text-teal" /> Copied</>
              ) : (
                <><Copy className="h-3.5 w-3.5" /> Copy all</>
              )}
            </Button>
          </div>
          {eft.bank_details ? (
            <pre
              data-testid="eft-bank-details"
              className="font-mono text-xs sm:text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap bg-muted/40 p-4 rounded-xl border border-border/60"
            >
              {eft.bank_details}
            </pre>
          ) : (
            <p className="font-body text-xs text-foreground/60 italic">
              Banking details have not been configured yet. Please email{' '}
              <a
                className="text-teal underline"
                href={`mailto:${eft.support_email || 'info@avzwholesale.co.za'}`}
              >
                {eft.support_email || 'info@avzwholesale.co.za'}
              </a>{' '}
              and quote your reference.
            </p>
          )}
        </div>

        {/* Email / Invoice actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <Button
            variant="ctaSecondary"
            onClick={handleInvoice}
            disabled={downloadingInvoice}
            data-testid="eft-download-invoice"
            className="rounded-full font-body text-sm gap-2"
          >
            {downloadingInvoice ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download invoice
          </Button>
          <a
            href={`mailto:${eft.support_email || 'info@avzwholesale.co.za'}?subject=Proof%20of%20Payment%20${encodeURIComponent(eft.reference)}`}
            data-testid="eft-email-pop"
          >
            <Button
              variant="ctaSecondary"
              className="rounded-full font-body text-sm gap-2 w-full"
            >
              <Mail className="h-4 w-4" />
              Email proof of payment
            </Button>
          </a>
        </div>

        <Link to="/profile">
          <Button
            data-testid="eft-finish-btn"
            variant="ctaPrimary"
            className="w-full rounded-full font-body text-sm font-semibold py-5 gap-2"
          >
            I've made the transfer — back to my orders <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
